import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import api from "../lib/api";
import { clearToken } from "../lib/tokenStorage";
import { Users, Plane, ArrowRight, Mail, Lock, Sparkles, KeyRound, X, Handshake, Download, Smartphone,
         Briefcase, LineChart, CalendarDays, Laptop2,
         Luggage, Globe, MapPin, Palmtree,
         GraduationCap, Sprout, Home, HeartHandshake, Languages } from "lucide-react";
import { usePwaInstall } from "../lib/usePwaInstall";
import { useBrand } from "../lib/brand";

// ------------------------------------------------------------
// Landing-page copy translations. Only kicks in when the Founder
// hasn't written CUSTOM hero copy (i.e. the field still equals the
// default). Anything customised via BrandingEditor takes precedence
// so a Founder isn't overridden by our auto-translation. Keys map
// to the language codes we surface in the picker.
// ------------------------------------------------------------
const LANG_LABELS = { en: "🇬🇧 EN", es: "🇪🇸 ES", fr: "🇫🇷 FR", pt: "🇵🇹 PT" };
const TRANSLATIONS = {
  en: {
    hero_line: "Sign in to your world",
    hero_accent: "world",
    hero_subtitle: "One command centre for the team, one welcoming portal for participants, and a dedicated door for our sending partners. Choose your door.",
    team_card_title: "Team sign in",
    team_card_blurb: "Founder · BDOM · PCWM · GEHM · SMCM — access your operations command centre.",
    participant_card_title: "Participant sign in",
    participant_card_blurb: "Welcome, traveller! Complete your C-form, book activities, request services and share feedback — all in one place.",
    partner_card_title: "Partner sign in",
    partner_card_blurb: "Universities · Sending organisations · Representatives — submit participant applications, book weekend tours, and grab pre-arrival materials.",
    welcome_kicker: "Welcome",
    forgot_link: "Forgot password?",
    email_label: "Email", password_label: "Password", sign_in: "Sign in",
  },
  es: {
    hero_line: "Inicia sesión en tu mundo",
    hero_accent: "mundo",
    hero_subtitle: "Un centro de mando para el equipo, un portal de bienvenida para los participantes y una puerta dedicada para nuestros socios. Elige tu puerta.",
    team_card_title: "Acceso Equipo",
    team_card_blurb: "Fundador · BDOM · PCWM · GEHM · SMCM — accede a tu centro de operaciones.",
    participant_card_title: "Acceso Participante",
    participant_card_blurb: "¡Bienvenido, viajero! Completa tu formulario, reserva actividades, solicita servicios y comparte comentarios — todo en un solo lugar.",
    partner_card_title: "Acceso Socio",
    partner_card_blurb: "Universidades · Organizaciones remitentes · Representantes — envía aplicaciones, reserva tours de fin de semana y obtén materiales previos a la llegada.",
    welcome_kicker: "Bienvenido",
    forgot_link: "¿Olvidaste tu contraseña?",
    email_label: "Correo", password_label: "Contraseña", sign_in: "Iniciar sesión",
  },
  fr: {
    hero_line: "Connectez-vous à votre monde",
    hero_accent: "monde",
    hero_subtitle: "Un centre de commande pour l'équipe, un portail d'accueil pour les participants, et une porte dédiée pour nos partenaires. Choisissez votre porte.",
    team_card_title: "Connexion Équipe",
    team_card_blurb: "Fondateur · BDOM · PCWM · GEHM · SMCM — accédez à votre centre d'opérations.",
    participant_card_title: "Connexion Participant",
    participant_card_blurb: "Bienvenue, voyageur ! Remplissez votre formulaire, réservez des activités, demandez des services et partagez vos retours — le tout au même endroit.",
    partner_card_title: "Connexion Partenaire",
    partner_card_blurb: "Universités · Organisations d'envoi · Représentants — soumettez des candidatures, réservez des tours et récupérez les documents pré-arrivée.",
    welcome_kicker: "Bienvenue",
    forgot_link: "Mot de passe oublié ?",
    email_label: "E-mail", password_label: "Mot de passe", sign_in: "Se connecter",
  },
  pt: {
    hero_line: "Entre no seu mundo",
    hero_accent: "mundo",
    hero_subtitle: "Um centro de comando para a equipe, um portal de boas-vindas para os participantes e uma porta dedicada para nossos parceiros. Escolha sua porta.",
    team_card_title: "Acesso da Equipe",
    team_card_blurb: "Fundador · BDOM · PCWM · GEHM · SMCM — acesse seu centro de operações.",
    participant_card_title: "Acesso do Participante",
    participant_card_blurb: "Bem-vindo, viajante! Complete seu formulário, reserve atividades, solicite serviços e compartilhe feedback — tudo em um só lugar.",
    partner_card_title: "Acesso do Parceiro",
    partner_card_blurb: "Universidades · Organizações de envio · Representantes — envie candidaturas, reserve tours de fim de semana e pegue materiais pré-chegada.",
    welcome_kicker: "Bem-vindo",
    forgot_link: "Esqueceu a senha?",
    email_label: "E-mail", password_label: "Senha", sign_in: "Entrar",
  },
};

// ------------------------------------------------------------
// PanelImageFader — small clip-art tile sitting top-left on each
// login panel. Rotates slowly (6s dwell, 2s crossfade) through 4
// relevant lucide icons. Zero network payload — icons ship with
// the bundle so nothing slows the login page.
// ------------------------------------------------------------
const FADER_SETS = {
  // Office / planning — for the Team panel (Founder · BDOM · PCWM · GEHM · SMCM)
  office: [
    { Icon: Briefcase, label: "operations" },
    { Icon: LineChart, label: "planning" },
    { Icon: Laptop2, label: "command centre" },
    { Icon: CalendarDays, label: "schedule" },
  ],
  // Travel — for the Participant panel
  travel: [
    { Icon: Plane, label: "journey" },
    { Icon: Luggage, label: "packed" },
    { Icon: Globe, label: "worldwide" },
    { Icon: Palmtree, label: "destination" },
  ],
  // Field / coordinator / community — for the Partner panel
  field: [
    { Icon: HeartHandshake, label: "partnership" },
    { Icon: GraduationCap, label: "students" },
    { Icon: Sprout, label: "impact" },
    { Icon: Home, label: "community" },
  ],
};

function PanelImageFader({ kind }) {
  const items = useMemo(() => FADER_SETS[kind] || FADER_SETS.travel, [kind]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let timer;
    const tick = () => { setIdx((i) => (i + 1) % items.length); };
    const start = () => { timer = window.setInterval(tick, 6000); };
    const stop = () => { if (timer) { window.clearInterval(timer); timer = null; } };
    const onVis = () => { stop(); if (!document.hidden) start(); };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [items.length]);

  return (
    <div
      className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden bg-white/12 backdrop-blur ring-1 ring-white/25 shadow-lg shadow-black/20 mb-4 flex items-center justify-center"
      aria-hidden="true"
      data-testid={`panel-fader-${kind}`}
    >
      {items.map(({ Icon, label }, i) => (
        <div
          key={label}
          className="absolute inset-0 flex items-center justify-center text-white"
          style={{
            opacity: i === idx ? 1 : 0,
            transform: i === idx ? "scale(1)" : "scale(0.85)",
            transition: "opacity 2000ms ease-in-out, transform 2000ms ease-in-out",
            willChange: "opacity, transform",
          }}
        >
          <Icon size={36} strokeWidth={1.6} />
        </div>
      ))}
    </div>
  );
}

function PanelForm({ variant, submit, busy, email, password, setEmail, setPassword, onForgot }) {
  const isTeam = variant === "team";
  return (
    <form onSubmit={submit} className="space-y-4" autoComplete="off" data-testid={`login-form-${variant}`}>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-semibold text-white/80 flex items-center gap-1"><Mail size={11}/> Email</label>
        <input
          data-testid={`${variant}-login-email`}
          type="email" autoComplete="username" required
          value={email} onChange={(e) => setEmail(e.target.value)}
          className="mt-1 w-full px-3 py-2.5 rounded-md bg-white text-slate-900 font-medium placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-white/50"
          placeholder={isTeam ? "you@aiigroup.com" : "you@example.com"}
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-semibold text-white/80 flex items-center gap-1"><Lock size={11}/> Password</label>
        <input
          data-testid={`${variant}-login-password`}
          type="password" autoComplete="current-password" required
          value={password} onChange={(e) => setPassword(e.target.value)}
          className="mt-1 w-full px-3 py-2.5 rounded-md bg-white text-slate-900 font-medium placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-white/50"
          placeholder="••••••••"
        />
      </div>
      <button
        data-testid={`${variant}-login-submit`}
        type="submit" disabled={busy}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md font-semibold transition disabled:opacity-60 bg-white text-brand hover:bg-white/90"
      >
        {busy ? "Signing in…" : <>Sign in <ArrowRight size={14}/></>}
      </button>
      <button
        type="button" onClick={onForgot} data-testid={`${variant}-forgot-link`}
        className="w-full text-center text-xs text-white/70 hover:text-white transition underline underline-offset-2 flex items-center justify-center gap-1"
      >
        <KeyRound size={10}/> Forgot password?
      </button>
    </form>
  );
}

function ForgotPasswordModal({ open, onClose, defaultEmail = "" }) {
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      // Server-managed participants get a specific error — surface it clearly.
      if (data?.ok === false) toast.error(data.message);
      else setSent(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  const close = () => { setEmail(""); setSent(false); onClose(); };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 relative shadow-2xl" data-testid="forgot-modal">
        <button onClick={close} data-testid="forgot-close" className="absolute top-3 right-3 text-slate-500 hover:text-slate-800"><X size={18}/></button>
        {!sent ? (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3"><KeyRound size={20}/></div>
              <h3 className="font-display text-xl font-semibold text-slate-900">Forgot your password?</h3>
              <p className="text-sm text-slate-600 mt-1">Enter the email you use to sign in. We&rsquo;ll email you a fresh password you can update from Profile after signing in.</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 flex items-center gap-1"><Mail size={11}/> Email</label>
              <input
                data-testid="forgot-email"
                type="email" autoComplete="username" required autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full px-3 py-2.5 rounded-md border border-slate-200 bg-white text-slate-900 font-medium placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500"
                placeholder="you@example.com"
              />
            </div>
            <button data-testid="forgot-submit" type="submit" disabled={busy}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md font-semibold transition disabled:opacity-60 bg-brand text-white hover:bg-brand-dark">
              {busy ? "Sending…" : <>Send new password <ArrowRight size={14}/></>}
            </button>
            <p className="text-[11px] text-slate-500">Participants provisioned by your coordinator: please ask them to <b>resend your credentials</b> instead.</p>
          </form>
        ) : (
          <div className="text-center py-2">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-3 text-2xl">✉️</div>
            <h3 className="font-display text-xl font-semibold text-slate-900">Check your inbox</h3>
            <p className="text-sm text-slate-600 mt-2">If <b>{email}</b> is registered, a new password is on its way. Sign in and change it from <b>Profile → Password</b>.</p>
            <button onClick={close} data-testid="forgot-close-done" className="mt-5 px-5 py-2 rounded-md font-semibold bg-brand text-white hover:bg-brand-dark">Got it</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const brand = useBrand();
  const { canInstall, installed, install } = usePwaInstall();
  const [iosHelp, setIosHelp] = useState(false);
  const [team, setTeam] = useState({ email: "", password: "", busy: false });
  const [part, setPart] = useState({ email: "", password: "", busy: false });
  const [partner, setPartner] = useState({ email: "", password: "", busy: false });
  const [forgot, setForgot] = useState({ open: false, defaultEmail: "" });
  // Language: participant can override; falls back to the Founder's
  // default_language on the brand config (English if unset). Cached in
  // localStorage so a returning visitor gets the same experience.
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem("aii.lang") || ""; } catch { return ""; }
  });
  const activeLang = lang || brand.default_language || "en";
  const t = useMemo(() => TRANSLATIONS[activeLang] || TRANSLATIONS.en, [activeLang]);
  const setLangPref = (code) => {
    setLang(code);
    try { localStorage.setItem("aii.lang", code); } catch { /* ignore */ }
  };
  // Fallback helper: use the Founder's custom copy when they've written
  // one, otherwise the translation for the current language.
  const cx = (customVal, tKey) => (customVal && customVal.trim() ? customVal : t[tKey]);

  const doLogin = async (variant, email, password, setter) => {
    setter((s) => ({ ...s, busy: true }));
    try {
      const res = await login(email, password);
      const role = res?.user?.role;
      if (variant === "team" && (role === "PARTICIPANT" || role === "PARTNER")) {
        const target = role === "PARTICIPANT" ? "Participant" : "Partner";
        toast.error(`This is a ${target.toLowerCase()} account — use the ${target} sign in.`);
        clearToken(); window.location.reload();
        return;
      }
      if (variant === "participant" && role !== "PARTICIPANT") {
        toast.error("This is not a participant account — use the correct sign-in tab.");
        clearToken(); window.location.reload();
        return;
      }
      if (variant === "partner" && role !== "PARTNER") {
        toast.error("This is not a partner account — use the correct sign-in tab.");
        clearToken(); window.location.reload();
        return;
      }
      toast.success(`Welcome to ${brand.name}`);
      const dest = role === "PARTICIPANT" ? "/portal" : role === "PARTNER" ? "/partner-portal" : "/dashboard";
      nav(dest);
    } catch {
      toast.error("Invalid credentials");
    } finally {
      setter((s) => ({ ...s, busy: false }));
    }
  };

  const isIos = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const doInstall = async () => {
    if (canInstall) {
      const outcome = await install();
      if (outcome === "accepted") toast.success("Installed — check your home screen");
    } else if (isIos) {
      setIosHelp(true);
    } else if (installed) {
      toast.info("Already installed on this device");
    } else {
      toast.info(`Your browser will show an install icon in the address bar — tap it to add ${brand.name} to your device`);
    }
  };
  const installBtnClass = "w-full mt-3 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold bg-white/15 hover:bg-white/25 border border-white/25 backdrop-blur text-white transition";
  const installBtn = (tid) => (
    <button type="button" onClick={doInstall} data-testid={tid} className={installBtnClass}>
      <Download size={13}/> {installed ? "Open installed app" : "Download the App"}
    </button>
  );

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#0d1f16]">
      {/* Background: full-bleed travel photo (mobile responsive via
          object-cover) → dark gradient wash for legibility → the
          original radial glows and dot-noise layered on top so cards
          keep the same brand feel. White-label ready: Founder can
          override `brand.hero_bg_url` from Settings to swap the photo
          per-tenant. */}
      <img
        src={brand.hero_bg_url || "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=2000&q=70"}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="login-hero-bg"
        loading="eager"
        fetchPriority="high"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#0d2b1e]/85 via-[#0f1714]/80 to-[#082014]/90"/>
      <div className="absolute inset-0 bg-black/40 sm:bg-black/30"/>
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#1B7A3E]/40 blur-3xl"/>
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-emerald-400/20 blur-3xl"/>
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)",
        backgroundSize: "3px 3px",
      }}/>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="p-6 sm:p-8 flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            {/* Logo is optional now — only rendered when a brand image
                URL is explicitly set. Keeps the header clean for the
                white-label / generic-ERP install where a tenant hasn't
                uploaded a logo yet. */}
            {brand.logo_url && (
              <img src={brand.logo_url} alt={brand.name} className="w-10 h-10 rounded-lg bg-white/10 p-1 object-contain" data-testid="brand-logo"/>
            )}
            <div>
              <div className="font-display text-lg font-semibold leading-none" data-testid="brand-wordmark">
                {brand.wordmark || brand.name}
                {!brand.hide_vendor_wordmark && <span className="text-emerald-300">®</span>}
              </div>
              {brand.tagline && <div className="text-[10px] tracking-widest uppercase text-white/60 mt-0.5">{brand.tagline}</div>}
            </div>
          </div>
          {brand.partner_matched && brand.partner_logo_url && (
            <div className="hidden md:flex items-center gap-2 text-white/80" data-testid="partner-cobrand">
              <span className="text-[10px] uppercase tracking-widest text-white/50">In partnership with</span>
              <img src={brand.partner_logo_url} alt={brand.partner_name || ""} className="h-8 w-auto object-contain bg-white/10 rounded px-1"/>
            </div>
          )}
          <a href="/stories" data-testid="stories-link" className="hidden sm:inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white transition">
            <Sparkles size={12}/> Success Stories
          </a>
          <div className="flex items-center gap-1" data-testid="lang-picker" title="Language">
            <Languages size={13} className="text-white/60"/>
            {Object.keys(LANG_LABELS).map(code => (
              <button
                key={code}
                data-testid={`lang-${code}`}
                type="button"
                onClick={() => setLangPref(code)}
                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${activeLang === code ? "bg-white text-slate-900" : "text-white/70 hover:text-white"}`}>
                {LANG_LABELS[code]}
              </button>
            ))}
          </div>
        </header>

        {/* Hero + panels */}
        <main className="flex-1 flex flex-col justify-center px-6 sm:px-10 pb-10">
          <div className="max-w-6xl mx-auto w-full">
            <div className="text-center mb-8 sm:mb-10 text-white">
              <div className="text-xs tracking-[0.35em] uppercase text-emerald-300/80 font-semibold">{t.welcome_kicker}</div>
              <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight" data-testid="brand-hero">
                {(() => {
                  const line = cx(brand.hero_line, "hero_line");
                  const accent = brand.hero_accent || t.hero_accent;
                  if (accent && line.includes(accent)) {
                    const [before, after] = line.split(accent);
                    return (<>{before}<span className="text-emerald-300">{accent}</span>{after}</>);
                  }
                  return line;
                })()}
              </h1>
              <p className="mt-4 text-white/70 max-w-2xl mx-auto text-sm sm:text-base">
                {cx(brand.hero_subtitle, "hero_subtitle")}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-5 sm:gap-6">
              {/* Team panel */}
              <div className="relative rounded-2xl overflow-hidden group" data-testid="team-panel">
                <div className="absolute inset-0 bg-gradient-to-br from-[#1B7A3E] via-[#175f31] to-[#0b3d1f]"/>
                <div className="absolute inset-0 opacity-30 mix-blend-overlay" style={{
                  backgroundImage: "radial-gradient(circle at 20% 10%, rgba(255,255,255,0.6), transparent 50%)",
                }}/>
                <div className="relative p-7 sm:p-8 text-white">
                  <PanelImageFader kind="office"/>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center"><Users size={20}/></div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-white/70">{brand.team_card_kicker || "For Team"}</div>
                      <div className="font-display text-xl font-semibold">{brand.team_card_title || "Team sign in"}</div>
                    </div>
                  </div>
                  <p className="text-sm text-white/80 mb-5">{brand.team_card_blurb || "Founder, Tour Manager, House Keeping, Projects and Placements — access your operations command centre."}</p>
                  <PanelForm variant="team" busy={team.busy}
                    email={team.email} password={team.password}
                    setEmail={(v) => setTeam((s) => ({ ...s, email: v }))}
                    setPassword={(v) => setTeam((s) => ({ ...s, password: v }))}
                    submit={(e) => { e.preventDefault(); doLogin("team", team.email, team.password, setTeam); }}
                    onForgot={() => setForgot({ open: true, defaultEmail: team.email })}/>
                  {installBtn("team-install")}
                </div>
              </div>

              {/* Participant panel */}
              <div className="relative rounded-2xl overflow-hidden group" data-testid="participant-panel">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500"/>
                <div className="absolute inset-0 opacity-25 mix-blend-overlay" style={{
                  backgroundImage: "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.7), transparent 50%)",
                }}/>
                <div className="relative p-7 sm:p-8 text-white">
                  <PanelImageFader kind="travel"/>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center"><Plane size={20}/></div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-white/80">{brand.participant_card_kicker || "For Participants"}</div>
                      <div className="font-display text-xl font-semibold">Participant sign in</div>
                    </div>
                  </div>
                  <p className="text-sm text-white/90 mb-5">{brand.participant_card_blurb || "Welcome, traveller! Complete your C-form, book activities, request services and share feedback — all in one place."}</p>
                  <PanelForm variant="participant" busy={part.busy}
                    email={part.email} password={part.password}
                    setEmail={(v) => setPart((s) => ({ ...s, email: v }))}
                    setPassword={(v) => setPart((s) => ({ ...s, password: v }))}
                    submit={(e) => { e.preventDefault(); doLogin("participant", part.email, part.password, setPart); }}
                    onForgot={() => setForgot({ open: true, defaultEmail: part.email })}/>
                  <div className="mt-4 text-[11px] text-white/70">{brand.participant_card_footnote || "Don't have credentials yet? Your coordinator will send them by email & WhatsApp."}</div>
                  {installBtn("participant-install")}
                </div>
              </div>

              {/* Partner panel */}
              <div className="relative rounded-2xl overflow-hidden group" data-testid="partner-panel">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-600"/>
                <div className="absolute inset-0 opacity-25 mix-blend-overlay" style={{
                  backgroundImage: "radial-gradient(circle at 50% 20%, rgba(255,255,255,0.6), transparent 50%)",
                }}/>
                <div className="relative p-7 sm:p-8 text-white">
                  <PanelImageFader kind="field"/>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center"><Handshake size={20}/></div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-white/80">{brand.partner_card_kicker || "For Sending Partners"}</div>
                      <div className="font-display text-xl font-semibold">{brand.partner_matched && brand.partner_name ? `Welcome, ${brand.partner_name}` : "Partner sign in"}</div>
                    </div>
                  </div>
                  <p className="text-sm text-white/90 mb-5">{brand.partner_card_blurb || "Universities · Sending organisations · Representatives — submit participant applications, book weekend tours, and grab pre-arrival materials."}</p>
                  <PanelForm variant="partner" busy={partner.busy}
                    email={partner.email} password={partner.password}
                    setEmail={(v) => setPartner((s) => ({ ...s, email: v }))}
                    setPassword={(v) => setPartner((s) => ({ ...s, password: v }))}
                    submit={(e) => { e.preventDefault(); doLogin("partner", partner.email, partner.password, setPartner); }}
                    onForgot={() => setForgot({ open: true, defaultEmail: partner.email })}/>
                  <div className="mt-4 text-[11px] text-white/70">{brand.partner_card_footnote || "Not a partner yet? Reach out to your platform contact — Founder / BDOM will create your credentials."}</div>
                  {installBtn("partner-install")}
                </div>
              </div>
            </div>

            <div className="text-center text-[11px] text-white/40 mt-8">
              {brand.footer_text || "Unauthorised access is prohibited."}
            </div>
          </div>
        </main>
      </div>
      <ForgotPasswordModal open={forgot.open} defaultEmail={forgot.defaultEmail} onClose={() => setForgot({ open: false, defaultEmail: "" })}/>
      {iosHelp && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setIosHelp(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl" data-testid="ios-install-help">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Smartphone size={18} className="text-brand"/><h3 className="font-display font-semibold text-lg">Install on iPhone / iPad</h3></div>
              <button onClick={() => setIosHelp(false)} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
            </div>
            <ol className="space-y-2 text-sm text-slate-700">
              <li><b>1.</b> In Safari, tap the <b>Share</b> icon <span className="inline-block px-1.5 py-0.5 bg-slate-100 rounded text-xs font-mono">⬆︎</span> at the bottom.</li>
              <li><b>2.</b> Scroll down and tap <b>Add to Home Screen</b>.</li>
              <li><b>3.</b> Confirm with <b>Add</b> — {brand.name} appears as an app icon on your home screen.</li>
            </ol>
            <p className="mt-3 text-[11px] text-slate-500">On Android / Windows / Mac, most browsers show an install prompt automatically — or use the address-bar install icon.</p>
            <button onClick={() => setIosHelp(false)} className="mt-4 w-full px-4 py-2 bg-brand text-white rounded-md font-semibold hover:bg-brand-dark">Got it</button>
          </div>
        </div>
      )}
    </div>
  );
}
